/**
 * Intel Packet case-file API — named packets, members, monitors, activity.
 */
import axios from 'axios';
import { authorizedFetch } from './authorizedFetch';

const baseUrl = process.env.REACT_APP_API_URL || '/api';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
});

export type PacketStatus =
  | 'draft'
  | 'collection'
  | 'analysis'
  | 'published'
  | 'monitoring'
  | 'closed';

export type MemberType =
  | 'mission'
  | 'geofence'
  | 'watchlist'
  | 'device'
  | 'adtech_id'
  | 'dfp_alert'
  | 'overwatch_article'
  | 'note'
  | 'related_packet'
  | 'area'
  | 'field_report'
  | 'person';

export type MonitorKind =
  | 'adtech'
  | 'dfp'
  | 'signals'
  | 'news'
  | 'geofence'
  | 'recurrence'
  | 'cotraveler'
  | 'pol'
  | 'anomaly'
  | 'enrichment';

export interface IntelPacket {
  id: number;
  name: string;
  status: PacketStatus;
  priority: string;
  course_id?: string | null;
  created_by: string;
  assigned_analyst?: string | null;
  confidence?: string | null;
  primary_subject_type?: string | null;
  primary_subject_id?: string | null;
  last_meaningful_at?: string | null;
  created_at: string;
  updated_at: string;
  members?: IntelMember[];
  monitors?: IntelMonitor[];
  latest_assessment?: IntelAssessment | null;
  focus?: {
    latitude: number;
    longitude: number;
    kind?: string;
    label?: string;
    zoom?: number;
  } | null;
}

export interface IntelMember {
  id: number;
  packet_id: number;
  member_type: MemberType;
  member_id: string;
  label?: string | null;
  is_primary: boolean;
  meta?: Record<string, unknown> | null;
  created_at: string;
}

export interface IntelMonitor {
  id: number;
  packet_id: number;
  kind: MonitorKind;
  enabled: boolean;
  schedule?: string | null;
  thresholds?: Record<string, unknown> | null;
  significance_floor: string;
  last_run_at?: string | null;
  last_error?: string | null;
}

export interface IntelActivityEvent {
  id: number;
  packet_id: number;
  event_type: string;
  significance: string;
  title: string;
  summary?: string | null;
  source: string;
  started_at: string;
  ended_at?: string | null;
  observation_count: number;
  entity_refs?: Record<string, unknown> | null;
  created_at: string;
}

export interface IntelInsight {
  id: number;
  packet_id: number;
  title: string;
  finding: string;
  category?: string | null;
  origin: string;
  validation: string;
  confidence?: string | null;
  evidence_refs?: Record<string, unknown> | null;
  related_entities?: Record<string, unknown> | null;
  created_by?: string | null;
  created_at: string;
  validated_by?: string | null;
  validated_at?: string | null;
}

export interface IntelAssessment {
  id: number;
  packet_id: number;
  version_label: string;
  executive_summary?: string | null;
  key_findings?: string | null;
  pol_assessment?: string | null;
  gaps?: string | null;
  confidence?: string | null;
  published_by?: string | null;
  published_at?: string | null;
  created_at: string;
}

export interface IntelPacketNote {
  id: number;
  packet_id: number;
  note_type: string;
  target_type: string;
  target_id?: string | null;
  content: string;
  created_by: string;
  created_at: string;
}

export interface IntelEvidence {
  id: number;
  packet_id: number;
  title: string;
  kind: string;
  source?: string | null;
  collected_at?: string | null;
  uploaded_by?: string | null;
  note_id?: number | null;
  insight_id?: number | null;
  body?: string | null;
  meta?: Record<string, unknown> | null;
  created_at: string;
}

export interface IntelAuditEntry {
  id: number;
  packet_id: number;
  action: string;
  actor: string;
  actor_kind: string;
  detail?: Record<string, unknown> | null;
  created_at: string;
}

export interface UnfiledNote {
  id: number;
  title?: string | null;
  content?: string | null;
  tags?: string[];
  created_by?: string;
  created_at?: string;
}

export interface PacketCreate {
  name: string;
  priority?: string;
  course_id?: string;
  assigned_analyst?: string;
  primary_subject_type?: MemberType;
  primary_subject_id?: string;
  primary_label?: string;
  start_collect?: boolean;
}

export interface PacketSubjectOptions {
  geofences: Array<{ id: number; name: string; course_id?: string | null; active?: boolean }>;
  devices: Array<{ device_id: string; label: string; entity_type?: string | null; course_id?: string | null }>;
  missions?: Array<{
    id: number;
    name: string;
    status: string;
    packet_id?: number | null;
    has_route: boolean;
    geofence_count: number;
    device_count: number;
  }>;
}

export interface SentinelInsightDraft {
  id: number;
  title: string;
  finding: string;
  category?: string | null;
  origin: string;
  validation: string;
  confidence?: string | null;
}

export interface SentinelAssessmentDraft {
  executive_summary: string;
  key_findings: string;
  pol_assessment: string;
  gaps: string;
  confidence: string;
}

export interface PromoteRequest {
  member_type: MemberType;
  member_id: string;
  label?: string;
  is_primary?: boolean;
  meta?: Record<string, unknown>;
}

export interface ResearchResult {
  success: boolean;
  query: string;
  summary: string;
  sources: Array<{ title: string; url: string; snippet: string }>;
  error?: string | null;
}

export const PACKET_STATUS_COLOR: Record<PacketStatus, string> = {
  draft: '#8b93a7',
  collection: '#3dd6f5',
  analysis: '#b388ff',
  published: '#5ee0a0',
  monitoring: '#f5b942',
  closed: '#6b7280',
};

export const PACKET_STATUS_LABELS: Record<PacketStatus, string> = {
  draft: 'Draft',
  collection: 'Collection',
  analysis: 'Analysis',
  published: 'Published',
  monitoring: 'Monitoring',
  closed: 'Closed',
};

export const PACKET_STATUS_HELP: Record<PacketStatus, string> = {
  draft: 'Requirements being scoped. Nothing is collected until you move to Collection.',
  collection: 'Sources tasked. Enabled monitors pull every 15 minutes on the worker VM, even if you log out.',
  analysis: 'Evaluate the take. Collection continues. Publish from here when the product is ready.',
  published: 'Assessment issued (a version, not the end of the packet). Next: Monitoring, or publish another version.',
  monitoring: 'Post-publication watch. Collection continues. Loop back to Analysis if new information reopens the question.',
  closed: 'Requirement satisfied or overtaken. No further collection or updates.',
};

/** Legal next statuses. Closed is allowed from any open state. Monitoring → Analysis is the only loop. */
export const PACKET_STATUS_TRANSITIONS: Record<PacketStatus, PacketStatus[]> = {
  draft: ['collection', 'closed'],
  collection: ['analysis', 'closed'],
  analysis: ['published', 'closed'],
  published: ['monitoring', 'closed'],
  monitoring: ['analysis', 'closed'],
  closed: [],
};

export const COLLECTING_STATUSES: PacketStatus[] = ['collection', 'analysis', 'published', 'monitoring'];
export const PUBLISH_FROM_STATUSES: PacketStatus[] = ['analysis', 'published'];

export function normalizePacketStatus(status?: string | null): PacketStatus {
  if (status === 'under_analysis') return 'analysis';
  if (status === 'continued_monitoring') return 'monitoring';
  if (status && status in PACKET_STATUS_LABELS) return status as PacketStatus;
  return 'draft';
}

export function statusAllowsCollect(status?: string | null): boolean {
  return COLLECTING_STATUSES.includes(normalizePacketStatus(status));
}

export function nextStatuses(status?: string | null): PacketStatus[] {
  const cur = normalizePacketStatus(status);
  const allowed = PACKET_STATUS_TRANSITIONS[cur] || [];
  return [cur, ...allowed.filter((s) => s !== cur)];
}

export const MONITOR_LABELS: Record<MonitorKind, string> = {
  geofence: 'Geofence',
  news: 'OSINT',
  adtech: 'AdTech',
  dfp: 'DFP',
  signals: 'Signals',
  recurrence: 'Recurrence',
  cotraveler: 'Co-traveler',
  pol: 'Pattern of Life',
  anomaly: 'Anomaly',
  enrichment: 'Enrichment',
};

/** Monitors that actually collect. Others stay in the schema but are hidden. */
export const WIRED_MONITOR_KINDS: MonitorKind[] = [
  'geofence',
  'dfp',
  'news',
  'adtech',
  'pol',
];

/** Compact subtitle — avoid a chip per monitor. */
export function formatPacketMonitorLine(packet: IntelPacket): string {
  const names = (packet.monitors || [])
    .filter((m) => m.enabled && WIRED_MONITOR_KINDS.includes(m.kind))
    .map((m) => MONITOR_LABELS[m.kind] || m.kind);
  const monitorBit =
    names.length === 0
      ? 'no monitors on'
      : names.length <= 2
        ? names.join(', ')
        : `${names.length} monitors on`;
  return `${packet.priority} · ${monitorBit}`;
}

export async function listPackets(params?: { course_id?: string; status?: string }): Promise<IntelPacket[]> {
  const response = await axios.get(`${baseUrl}/intel-packets`, { params, headers: getAuthHeaders() });
  return response.data;
}

export async function getPacket(id: number): Promise<IntelPacket> {
  const response = await axios.get(`${baseUrl}/intel-packets/${id}`, { headers: getAuthHeaders() });
  return response.data;
}

export async function createPacket(body: PacketCreate): Promise<IntelPacket> {
  const response = await axios.post(`${baseUrl}/intel-packets`, body, { headers: getAuthHeaders() });
  return response.data;
}

export async function findPacketsBySubject(memberType: MemberType, memberId: string): Promise<IntelPacket[]> {
  const response = await axios.get(`${baseUrl}/intel-packets/by-subject`, {
    params: { member_type: memberType, member_id: memberId },
    headers: getAuthHeaders(),
  });
  return response.data;
}

export async function getSubjectOptions(): Promise<PacketSubjectOptions> {
  const response = await axios.get(`${baseUrl}/intel-packets/subject-options`, { headers: getAuthHeaders() });
  return response.data;
}

export async function sentinelInsights(packetId: number): Promise<{ ok: boolean; insights: SentinelInsightDraft[] }> {
  const response = await axios.post(`${baseUrl}/intel-packets/${packetId}/sentinel/insights`, {}, { headers: getAuthHeaders() });
  return response.data;
}

export async function sentinelAssessment(packetId: number): Promise<{ ok: boolean; draft: SentinelAssessmentDraft }> {
  const response = await axios.post(`${baseUrl}/intel-packets/${packetId}/sentinel/assessment`, {}, { headers: getAuthHeaders() });
  return response.data;
}

export async function sentinelAsk(packetId: number, question: string): Promise<{ ok: boolean; answer: string }> {
  const response = await axios.post(
    `${baseUrl}/intel-packets/${packetId}/sentinel/ask`,
    { question },
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export async function updatePacket(id: number, body: Partial<IntelPacket>): Promise<IntelPacket> {
  const response = await axios.patch(`${baseUrl}/intel-packets/${id}`, body, { headers: getAuthHeaders() });
  return response.data;
}

export async function deletePacket(id: number): Promise<void> {
  await axios.delete(`${baseUrl}/intel-packets/${id}`, { headers: getAuthHeaders() });
}

export async function promoteMember(packetId: number, body: PromoteRequest): Promise<IntelPacket> {
  const response = await axios.post(`${baseUrl}/intel-packets/${packetId}/promote`, body, { headers: getAuthHeaders() });
  return response.data;
}

export interface PacketMapPoint {
  latitude: number;
  longitude: number;
  label: string;
  entityId?: string;
  kind?: string;
  visits?: number;
  duration?: number;
  timestamp?: string;
}

export interface PacketMapPolygon {
  label: string;
  entityId?: string;
  coordinates: number[][];
}

export interface PacketMapLayers {
  roster: PacketMapPoint[];
  evidence?: PacketMapPoint[];
  activity: PacketMapPoint[];
  pol: PacketMapPoint[];
  insights: PacketMapPoint[];
  geofences?: PacketMapPolygon[];
}

export interface HistoricPolSnapshot {
  queried_at: string;
  lookback_days: number;
  scope: string;
  dwells: number;
  days: number;
  places: Array<{
    name: string;
    latitude: number;
    longitude: number;
    dwells: number;
    days: number;
    minutes: number;
    in_fence: boolean;
  }>;
  new_aois: Array<{
    name: string;
    latitude: number;
    longitude: number;
    dwells: number;
    days: number;
    minutes?: number;
    in_fence?: boolean;
  }>;
  observations_stored?: number;
}

export async function updateMember(
  packetId: number,
  memberId: number,
  body: { label?: string; notes?: string; excluded?: boolean; is_primary?: boolean; scope?: string; lookback_days?: number },
): Promise<IntelPacket> {
  const response = await axios.patch(`${baseUrl}/intel-packets/${packetId}/members/${memberId}`, body, { headers: getAuthHeaders() });
  return response.data;
}

export async function removeMember(packetId: number, memberId: number): Promise<IntelPacket> {
  const response = await axios.delete(`${baseUrl}/intel-packets/${packetId}/members/${memberId}`, { headers: getAuthHeaders() });
  return response.data;
}

export async function runHistoricPol(
  packetId: number,
  memberId: number,
  body?: { lookback_days?: number; scope?: string },
): Promise<{ ok: boolean; snapshot: HistoricPolSnapshot; packet: IntelPacket }> {
  const response = await axios.post(
    `${baseUrl}/intel-packets/${packetId}/members/${memberId}/historic-pol`,
    body || {},
    { headers: getAuthHeaders() },
  );
  return response.data;
}

export async function getPacketMapLayers(packetId: number): Promise<PacketMapLayers> {
  const response = await axios.get(`${baseUrl}/intel-packets/${packetId}/map-layers`, { headers: getAuthHeaders() });
  return response.data;
}

export async function listMonitors(packetId: number): Promise<IntelMonitor[]> {
  const response = await axios.get(`${baseUrl}/intel-packets/${packetId}/monitors`, { headers: getAuthHeaders() });
  return response.data;
}

export async function patchMonitors(
  packetId: number,
  body: Array<{
    kind: MonitorKind;
    enabled?: boolean;
    significance_floor?: string;
    thresholds?: Record<string, unknown>;
  }>,
): Promise<IntelMonitor[]> {
  const response = await axios.patch(`${baseUrl}/intel-packets/${packetId}/monitors`, body, { headers: getAuthHeaders() });
  return response.data;
}

export async function collectNow(packetId: number): Promise<{ ok: boolean; collected: Record<string, number> }> {
  const response = await axios.post(`${baseUrl}/intel-packets/${packetId}/collect`, {}, { headers: getAuthHeaders() });
  return response.data;
}

export async function listActivity(packetId: number, params?: { source?: string; significance?: string }): Promise<IntelActivityEvent[]> {
  const response = await axios.get(`${baseUrl}/intel-packets/${packetId}/activity`, { params, headers: getAuthHeaders() });
  return response.data;
}

export interface IntelActivityPlayer {
  id: string;
  kind: string;
  label: string;
  count: number;
  detail?: string | null;
  last_at?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface IntelActivityDrilldown {
  event: IntelActivityEvent;
  summary: string;
  players: IntelActivityPlayer[];
}

export async function listActivityObservations(packetId: number, eventId: number): Promise<IntelActivityDrilldown> {
  const response = await axios.get(`${baseUrl}/intel-packets/${packetId}/activity/${eventId}/observations`, { headers: getAuthHeaders() });
  return response.data;
}

export async function listInsights(packetId: number): Promise<IntelInsight[]> {
  const response = await axios.get(`${baseUrl}/intel-packets/${packetId}/insights`, { headers: getAuthHeaders() });
  return response.data;
}

export async function createInsight(packetId: number, body: { title: string; finding: string; category?: string; origin?: string }): Promise<IntelInsight> {
  const response = await axios.post(`${baseUrl}/intel-packets/${packetId}/insights`, body, { headers: getAuthHeaders() });
  return response.data;
}

export async function validateInsight(packetId: number, insightId: number, validation: 'accepted' | 'rejected' | 'draft'): Promise<void> {
  await axios.post(`${baseUrl}/intel-packets/${packetId}/insights/${insightId}/validate`, { validation }, { headers: getAuthHeaders() });
}

export async function listAssessments(packetId: number): Promise<IntelAssessment[]> {
  const response = await axios.get(`${baseUrl}/intel-packets/${packetId}/assessments`, { headers: getAuthHeaders() });
  return response.data;
}

export async function createAssessment(packetId: number, body: {
  version_label: string;
  executive_summary?: string;
  key_findings?: string;
  pol_assessment?: string;
  gaps?: string;
  confidence?: string;
  publish?: boolean;
}): Promise<IntelAssessment> {
  const response = await axios.post(`${baseUrl}/intel-packets/${packetId}/assessments`, body, { headers: getAuthHeaders() });
  return response.data;
}

export async function listPacketNotes(packetId: number): Promise<IntelPacketNote[]> {
  const response = await axios.get(`${baseUrl}/intel-packets/${packetId}/notes`, { headers: getAuthHeaders() });
  return response.data;
}

export async function addPacketNote(packetId: number, body: { content: string; note_type?: string; target_type?: string }): Promise<IntelPacketNote> {
  const response = await axios.post(`${baseUrl}/intel-packets/${packetId}/notes`, body, { headers: getAuthHeaders() });
  return response.data;
}

export async function listEvidence(packetId: number): Promise<IntelEvidence[]> {
  const response = await axios.get(`${baseUrl}/intel-packets/${packetId}/evidence`, { headers: getAuthHeaders() });
  return response.data;
}

export async function addEvidence(packetId: number, body: { title: string; kind?: string; source?: string; body?: string; meta?: Record<string, unknown> }): Promise<IntelEvidence> {
  const response = await axios.post(`${baseUrl}/intel-packets/${packetId}/evidence`, body, { headers: getAuthHeaders() });
  return response.data;
}

export async function deleteEvidence(packetId: number, evidenceId: number): Promise<void> {
  await axios.delete(`${baseUrl}/intel-packets/${packetId}/evidence/${evidenceId}`, { headers: getAuthHeaders() });
}

const photoCache = new Map<string, string>();
const photoInflight = new Map<string, Promise<string>>();

function photoCacheKey(reportId: string, variant: 'original' | 'annotated'): string {
  return `${reportId}:${variant}`;
}

/** Drop a cached photo so the next fetch hits the API again. Does not revoke — another card may still be showing it. */
export function evictFieldReportPhoto(
  reportId: string,
  variant: 'original' | 'annotated' = 'original',
): void {
  photoCache.delete(photoCacheKey(reportId, variant));
}

/**
 * Direct JPEG URL for an <img>. Blob URLs went blank in the dock after
 * onLoad — global `img { height: auto }` plus a revoked object URL left the
 * well empty with the pixel size still in the overlay. EventSource already
 * sends the short-lived token this way; the photo route accepts it.
 */
export function fieldReportPhotoHref(
  reportId: string,
  variant: 'original' | 'annotated' = 'original',
  bust?: number,
): string {
  const qs = new URLSearchParams({ variant });
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null;
  if (token) qs.set('access_token', token);
  if (bust) qs.set('t', String(bust));
  return `${baseUrl}/field-reports/${encodeURIComponent(reportId)}/photo?${qs.toString()}`;
}

/**
 * Field-report JPEG as a blob URL, one per report+variant for the tab session.
 *
 * Callers must not revoke the URL. The card, map popup, dossier, and evidence
 * strip all ask for the same photo; revoking in any of them blanked the well
 * after onLoad had already written the pixel size into the overlay.
 */
export async function fetchFieldReportPhoto(
  reportId: string,
  variant: 'original' | 'annotated' = 'original',
): Promise<string> {
  const key = photoCacheKey(reportId, variant);
  const cached = photoCache.get(key);
  if (cached) return cached;
  const inflight = photoInflight.get(key);
  if (inflight) return inflight;

  const request = (async () => {
    const response = await authorizedFetch(
      `${baseUrl}/field-reports/${encodeURIComponent(reportId)}/photo?variant=${encodeURIComponent(variant)}`,
    );
    if (!response.ok) {
      throw new Error(`photo ${response.status}`);
    }
    const raw = await response.blob();
    if (!raw || !raw.size) {
      throw new Error('empty photo');
    }
    const type = (raw.type || '').toLowerCase();
    if (type.includes('json') || type.includes('html')) {
      throw new Error('photo failed');
    }
    const blob = type.startsWith('image/') ? raw : new Blob([await raw.arrayBuffer()], { type: 'image/jpeg' });
    const objectUrl = URL.createObjectURL(blob);
    photoCache.set(key, objectUrl);
    return objectUrl;
  })();

  photoInflight.set(key, request);
  try {
    return await request;
  } finally {
    photoInflight.delete(key);
  }
}

export async function research(packetId: number, query: string, saveAs?: 'evidence' | 'insight'): Promise<ResearchResult> {
  const response = await axios.post(`${baseUrl}/intel-packets/${packetId}/research`, { query, save_as: saveAs }, { headers: getAuthHeaders() });
  return response.data;
}

export async function listEntities(packetId: number): Promise<{ primary: IntelMember | null; by_type: Record<string, IntelMember[]> }> {
  const response = await axios.get(`${baseUrl}/intel-packets/${packetId}/entities`, { headers: getAuthHeaders() });
  return response.data;
}

export async function listAudit(packetId: number): Promise<IntelAuditEntry[]> {
  const response = await axios.get(`${baseUrl}/intel-packets/${packetId}/audit`, { headers: getAuthHeaders() });
  return response.data;
}

export async function listUnfiledNotes(): Promise<UnfiledNote[]> {
  const response = await axios.get(`${baseUrl}/intel-packets/unfiled-notes`, { headers: getAuthHeaders() });
  return response.data;
}

export async function importNotes(packetId: number, noteIds: number[]): Promise<{ imported: number }> {
  const response = await axios.post(`${baseUrl}/intel-packets/${packetId}/import-notes`, { note_ids: noteIds }, { headers: getAuthHeaders() });
  return response.data;
}

export async function exportPacket(packetId: number, format: 'html' | 'pdf' = 'html'): Promise<void> {
  const response = await axios.get(`${baseUrl}/intel-packets/${packetId}/export`, {
    params: { format },
    headers: getAuthHeaders(),
    responseType: 'blob',
  });
  const url = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `intel-packet-${packetId}.${format}`;
  a.click();
  URL.revokeObjectURL(url);
}

export default {
  listPackets,
  getPacket,
  createPacket,
  findPacketsBySubject,
  getSubjectOptions,
  sentinelInsights,
  sentinelAssessment,
  sentinelAsk,
  updatePacket,
  deletePacket,
  promoteMember,
  listMonitors,
  patchMonitors,
  collectNow,
  listActivity,
  listActivityObservations,
  listInsights,
  createInsight,
  validateInsight,
  listAssessments,
  createAssessment,
  listPacketNotes,
  addPacketNote,
  listEvidence,
  addEvidence,
  deleteEvidence,
  fetchFieldReportPhoto,
  research,
  listEntities,
  listAudit,
  listUnfiledNotes,
  importNotes,
  exportPacket,
};
